// src/hooks/useDashboardCards.ts
import { useState, useCallback, useEffect } from "react";
import { CardData } from "../types/card.types";
import { cardService } from "../services/card.service";

const PAGE_SIZE = 10;

interface UseDashboardCardsReturn {
  cards: CardData[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: Error | null;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
}

export const useDashboardCards = (userId?: string): UseDashboardCardsReturn => {
  const [cards, setCards] = useState<CardData[]>([]);
  const [loading, setLoading] = useState(Boolean(userId));
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(0);
  const [error, setError] = useState<Error | null>(null);

  const loadCards = useCallback(
    async (pageNum: number, isInitial = false) => {
      if (!userId) {
        setCards([]);
        setLoading(false);
        setLoadingMore(false);
        setHasMore(false);
        return;
      }

      try {
        if (isInitial) setLoading(true);
        else setLoadingMore(true);

        setError(null);

        const { data, hasMore: moreAvailable } = await cardService.getUserCardsPaginated(
          userId,
          pageNum,
          PAGE_SIZE
        );

        setCards((prev) => (isInitial ? data : [...prev, ...data]));
        setHasMore(moreAvailable);
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [userId]
  );

  useEffect(() => {
    setPage(0);
    void loadCards(0, true);
  }, [loadCards]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loadingMore) return;

    const nextPage = page + 1;
    setPage(nextPage);
    await loadCards(nextPage);
  }, [page, hasMore, loadingMore, loadCards]);

  const refresh = useCallback(async () => {
    setPage(0);
    await loadCards(0, true);
  }, [loadCards]);

  return {
    cards,
    loading,
    loadingMore,
    hasMore,
    error,
    loadMore,
    refresh,
  };
};

export default useDashboardCards;
